import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { CalendarClock, ExternalLink } from "lucide-react";
import { AppointmentDetailsDialog } from "@/components/calendar/AppointmentDetailsDialog";
import { OwnerStayPlansSection } from "@/components/packages/OwnerStayPlansSection";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";
import { usePermissionsMap } from "@/hooks/usePermissionsMap";
import { supabase } from "@/integrations/supabase/client";
import {
  PATIENT_PACKAGE_SELECT,
  normalizePatientPackage,
  type PatientPackageFinancialStatus,
  type PatientPackageSummary,
} from "@/hooks/usePatientPackages";

interface OwnerPackagesPanelProps {
  ownerId: string;
  clinicId: string;
  ownerName?: string | null;
  petIds: string[];
  pets: Array<{ id: string; name: string }>;
}

type OwnerPatientPackageSummary = PatientPackageSummary & {
  pet: {
    id: string;
    name: string | null;
  } | null;
};

type PatientPackageConsumptionRow = {
  id: string;
  appointment_id: string;
  sessions_to_consume: number;
  appointment: {
    id: string;
    starts_at: string | null;
    status: string | null;
  } | null;
};

const getDisplayLocale = (language: string) =>
  language.startsWith("pt") ? "pt-BR" : "en-US";

const formatCurrency = (value: number, language: string) =>
  new Intl.NumberFormat(getDisplayLocale(language), {
    style: "currency",
    currency: "BRL",
  }).format(value);

const formatDate = (value: string, language: string) =>
  new Intl.DateTimeFormat(getDisplayLocale(language)).format(new Date(value));

const formatDateTime = (value: string, language: string) =>
  new Intl.DateTimeFormat(getDisplayLocale(language), {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));

const getFinancialBadge = (status: PatientPackageFinancialStatus) => {
  if (status === "pago") {
    return {
      labelKey: "packagePanel.paid",
      className: "bg-emerald-700 text-white",
    };
  }
  if (status === "parcial") {
    return {
      labelKey: "packagePanel.partial",
      className: "bg-amber-500 text-white",
    };
  }
  return {
    labelKey: "packagePanel.pending",
    className: "bg-slate-600 text-white",
  };
};

function PackageConsumptions({
  packageId,
  onViewAppointment,
}: {
  packageId: string;
  onViewAppointment: (appointmentId: string) => void;
}) {
  const { t, i18n } = useTranslation("patients");
  const { data: consumptions = [], isLoading } = useQuery({
    queryKey: ["owner-patient-package-consumptions", packageId],
    queryFn: async (): Promise<PatientPackageConsumptionRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("appointment_patient_packages")
        .select(`
          id,
          appointment_id,
          sessions_to_consume,
          consumed_at,
          appointment:appointments(
            id,
            starts_at,
            status
          )
        `)
        .eq("patient_package_id", packageId)
        .eq("consumption_status", "consumed")
        .order("consumed_at", { ascending: false });

      if (error) throw error;

      return (data ?? []) as PatientPackageConsumptionRow[];
    },
  });

  if (isLoading) return <Skeleton className="mt-4 h-16 w-full" />;

  return (
    <div className="mt-4 rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4" aria-hidden="true" />
        <p className="font-medium text-foreground">{t("packagesTab.consumptions")}</p>
      </div>
      {consumptions.length === 0 ? (
        <p className="mt-1">{t("packagesTab.consumptionsEmpty")}</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {consumptions.map((consumption) => (
            <li
              key={consumption.id}
              className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <span>
                {consumption.appointment?.starts_at
                  ? formatDateTime(
                      consumption.appointment.starts_at,
                      i18n.language,
                    )
                  : t("packagePanel.linkedToAppointment")}
                {consumption.sessions_to_consume > 1
                  ? t("packagesTab.consumptionMultiplier", {
                      count: consumption.sessions_to_consume,
                    })
                  : ""}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onViewAppointment(consumption.appointment_id)}
              >
                {t("packagePanel.viewAppointment")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function OwnerPackagesPanel({
  ownerId,
  clinicId,
  petIds,
  pets,
}: OwnerPackagesPanelProps) {
  const { t, i18n } = useTranslation("patients");
  const { hasDaycare, hasHotel } = useDaycareHotelAccess();
  const { data: permissions } = usePermissionsMap();
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(
    null,
  );

  const { data: patientPackages = [], isLoading } = useQuery({
    queryKey: ["owner-patient-packages", clinicId, petIds],
    enabled: Boolean(clinicId && petIds.length > 0),
    queryFn: async (): Promise<OwnerPatientPackageSummary[]> => {
      if (!clinicId || petIds.length === 0) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("patient_packages")
        .select(`
          ${PATIENT_PACKAGE_SELECT},
          pet:pets(
            id,
            name
          )
        `)
        .eq("clinic_id", clinicId)
        .in("pet_id", petIds)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return ((data ?? []) as Array<
        Parameters<typeof normalizePatientPackage>[0] & {
          pet?: { id: string; name: string | null } | null;
        }
      >).map((row) => ({
        ...normalizePatientPackage(row),
        pet: row.pet ?? null,
      }));
    },
  });

  const { activePackages, historyPackages } = useMemo(() => {
    const ordered = [...patientPackages].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    return {
      activePackages: ordered.filter(
        (pkg) =>
          !pkg.catalog_package?.controls_sessions || pkg.sessions_remaining > 0,
      ),
      historyPackages: ordered.filter(
        (pkg) =>
          (pkg.catalog_package?.controls_sessions ?? true) &&
          pkg.sessions_remaining <= 0,
      ),
    };
  }, [patientPackages]);

  const title = hasDaycare
    ? t("packagesTab.ownerTitleWithPlans")
    : t("packagesTab.ownerTitle");

  const renderPackageCard = (pkg: OwnerPatientPackageSummary) => {
    const financialBadge = getFinancialBadge(pkg.status_financeiro);
    const controlsSessions = pkg.catalog_package?.controls_sessions ?? true;

    return (
      <div key={pkg.id} className="rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-base font-semibold text-foreground">
                {pkg.catalog_package?.item?.name ?? t("packagePanel.package")}
              </h4>
              {pkg.pet?.name ? <Badge variant="outline">{pkg.pet.name}</Badge> : null}
              <Badge className={financialBadge.className}>
                {t(financialBadge.labelKey)}
              </Badge>
            </div>
          </div>

          <Button asChild variant="outline" size="sm">
            <Link to={`/patients/pets/${pkg.pet_id}/packages`}>
              <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("packagePanel.openPatient")}
            </Link>
          </Button>
        </div>

        <div className="mt-4 grid gap-3 text-sm md:grid-cols-3">
          <div className="grid gap-3 md:grid-cols-3">
            {controlsSessions ? (
              <>
                <div>
                  <p className="text-muted-foreground">
                    {t("packagePanel.sessions")}
                  </p>
                  <p className="font-medium">
                    {pkg.sessions_used}/{pkg.sessions_total}{" "}
                    {t("packagePanel.usedSessions", {
                      count: pkg.sessions_used,
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">
                    {t("packagePanel.balance")}
                  </p>
                  <p className="font-medium">{pkg.sessions_remaining}</p>
                </div>
              </>
            ) : (
              <div>
                <p className="text-muted-foreground">
                  {t("packagePanel.sessions")}
                </p>
                <p className="font-medium">
                  {t("packagePanel.noSessionControl")}
                </p>
              </div>
            )}
            <div>
              <p className="text-muted-foreground">
                {t("packagePanel.totalValue")}
              </p>
              <p className="font-medium">
                {formatCurrency(pkg.full_price, i18n.language)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">
                {t("packagePanel.amountPaid")}
              </p>
              <p className="font-medium">
                {formatCurrency(pkg.amount_paid, i18n.language)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">
                {t("packagePanel.outstanding")}
              </p>
              <p className="font-medium">
                {formatCurrency(pkg.outstanding_amount, i18n.language)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">
                {t("packagePanel.createdAt")}
              </p>
              <p className="font-medium">
                {formatDate(pkg.created_at, i18n.language)}
              </p>
            </div>
          </div>
        </div>

        {controlsSessions ? (
          <PackageConsumptions
            packageId={pkg.id}
            onViewAppointment={setSelectedAppointmentId}
          />
        ) : null}

        {pkg.origin === "appointment" ? (
          <div className="mt-4 rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">
              {t("packagePanel.appointmentOrigin")}
            </p>
            <p>
              {pkg.created_from_appointment?.starts_at
                ? formatDateTime(
                    pkg.created_from_appointment.starts_at,
                    i18n.language,
                  )
                : t("packagePanel.linkedToAppointment")}
            </p>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPackageList = (
    packages: OwnerPatientPackageSummary[],
    emptyLabel: string,
  ) => {
    if (packages.length === 0) {
      return (
        <div className="rounded-lg border border-border bg-muted/20 px-4 py-5 text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {packages.map((pkg) => renderPackageCard(pkg))}
      </div>
    );
  };

  return (
    <>
      <div className="space-y-6">
        <section className="space-y-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">
                  {t("packagesTab.active")}
                </h4>
                {renderPackageList(
                  activePackages,
                  t("packagesTab.emptyOwnerActive"),
                )}
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">
                  {t("packagesTab.history")}
                </h4>
                {renderPackageList(
                  historyPackages,
                  t("packagesTab.emptyOwnerHistory"),
                )}
              </div>
            </div>
          )}
        </section>

        {hasDaycare || hasHotel ? (
          <OwnerStayPlansSection
            clinicId={clinicId}
            ownerId={ownerId}
            pets={pets}
            hasDaycare={hasDaycare}
            hasHotel={hasHotel}
            canContract={Boolean(permissions?.patients?.update)}
          />
        ) : null}
      </div>

      <AppointmentDetailsDialog
        appointmentId={selectedAppointmentId ?? ""}
        open={Boolean(selectedAppointmentId)}
        onOpenChange={(open) => {
          if (!open) setSelectedAppointmentId(null);
        }}
      />
    </>
  );
}
